import { EventEmitter } from 'events';
import { IFluidObject } from '@fluidframework/core-interfaces';
import {
  InsertComponentRequestData,
  ProvideNamespaceLogger,
  DataRequestProcessor,
  DataRequestCreator,
  DataRequestCallback,
  MenuViewProvider,
  Audience,
  ProvideMenuViewProvider,
  DiscoverData
} from '@fluidx/office-fluid-types';
import { IContainerRuntime } from '@fluidframework/container-runtime-definitions';
import { ITelemetryBaseLogger } from '@fluidframework/common-definitions';
import { NamespaceLoggerAdapter, resolveLocale, Locale } from '@fluidx/utilities';
import { SharedContainerServiceFactory } from '@fluidx/loop-sdk';
import { AsyncProvider } from '@fluidx/loop-types';
import { IFluidDependencySynthesizer } from '@fluidframework/synthesize';

/**
 * DiscoverComponent is the component that manages Discover, the one-stop shop for discovering and
 * executing component commands and inserting components from different available registries.
 *
 * For other components to participate in the menu results, they are expected to be processors of
 * DiscoverData. To invoke the component, other components can act as producers of InsertComponentRequestData and
 * allow Discover to invoke itself on some trigger (a keypress, button click, etc.).
 */
export class DiscoverComponent extends EventEmitter implements DataRequestProcessor, DataRequestCreator {
  public readonly supportedDataTypes: (keyof IFluidObject)[] = ['ComponentProduceInsertComponentRequestData'];
  private logger: ITelemetryBaseLogger | undefined;
  private producerCallback: DataRequestCallback<DiscoverData> | undefined;

  private menuViewProvider: Promise<MenuViewProvider | undefined>;
  private audience: Audience | undefined;
  private locale: Locale | undefined;

  public get DataRequestProcessor() {
    return this;
  }

  public get DataRequestCreator() {
    return this;
  }

  public registerDataRequestCallback(producerCallback: DataRequestCallback<DiscoverData>): void {
    this.producerCallback = producerCallback;
  }

  constructor(
    runtime: IContainerRuntime,
    private readonly providers: AsyncProvider<ProvideNamespaceLogger & ProvideMenuViewProvider>
  ) {
    super();

    this.providers.NamespaceLogger?.then(
      (logger) => (this.logger = logger ? new NamespaceLoggerAdapter('Discover', logger) : undefined)
    ).catch(() => {});

    // Note: Don't access the dependency synthesizer in this manner in general - this is back compat for mobile, estimate for removal ~3/5/22
    const dependencySynthesizer: IFluidDependencySynthesizer | undefined = runtime.scope.IFluidDependencySynthesizer;
    const menuViewProvider = dependencySynthesizer?.getProvider('MenuViewProvider');
    this.menuViewProvider =
      menuViewProvider === undefined || menuViewProvider instanceof Function
        ? Promise.resolve(undefined)
        : Promise.resolve(menuViewProvider);

    // TODO: When mobile has implemented the MenuViewProvider interface, add this back in.
    // this.menuViewProvider = this.providers.MenuViewProvider || Promise.resolve(undefined);

    this.audience = runtime.scope.HostContext?.audience;
    this.locale = resolveLocale(runtime.scope.HostContext?.hostLocaleContext?.uiLanguage);
  }

  public static serviceFactory = new SharedContainerServiceFactory(DiscoverComponent, {
    NamespaceLogger: 'NamespaceLogger',
    MenuViewProvider: 'MenuViewProvider'
  });

  public async processDataRequest(interfaceName: keyof IFluidObject, data: any): Promise<{}> {
    if (interfaceName === 'ComponentProduceInsertComponentRequestData') {
      this.handleInsertComponentRequestData(data);
    }
    return {};
  }

  private handleInsertComponentRequestData(insertRequestData: InsertComponentRequestData): void {
    // TODO: Maybe we should filter Discover items based on InsertComponentRequestData requestedDataType.
    const producerCallback = this.producerCallback;

    if (producerCallback !== undefined) {
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      import(/* webpackChunkName: "renderDiscoverMenu", webpackPrefetch: true */ '../view/renderDiscoverMenu').then(
        async (m) => {
          const menuViewProvider = await this.menuViewProvider;
          await m.renderDiscoverMenu({
            insertRequestData,
            producerCallback,
            logger: this.logger,
            audience: this.audience,
            menuViewProvider,
            locale: this.locale
          });
        }
      );
    }
  }
}

export const serviceFactory = DiscoverComponent.serviceFactory;
